import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Plus, Search, Edit, Trash2, ChevronLeft, ChevronRight, Users, Download, Copy, Printer, Columns } from 'lucide-react'
import toast from 'react-hot-toast'
import { PageHeader } from '@/components/shared/PageHeader'
import { ListLoader } from '@/components/shared/ListLoader'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAuthStore } from '@/store/auth.store'
import { empAddressService } from '@/services/empAddressService'
import type { EmpAddressListItem } from '@/api/types'
import { showDeleteConfirm, showError, showSuccess } from '@/lib/swal'
import { useEmployeeListScope } from '@/hooks/useEmployeeListScope'
import { EmployeeListScopeDialog } from '@/components/employee-scope/EmployeeListScopeDialog'

export const EmpAddressList: React.FC = () => {
  const { t } = useTranslation('common')
  const ta = (key: string) => t(`empAddress.${key}`)
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId
  const { empId, pickerOpen, confirmEmployee, openEmployeePicker, handlePickerOpenChange } = useEmployeeListScope()

  const [addresses, setAddresses] = useState<EmpAddressListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalRecords, setTotalRecords] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [pendingAdd, setPendingAdd] = useState(false)
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({
    homePhone: true,
    addressDetail: true,
    createdDate: true,
    action: true,
  })

  useEffect(() => {
    if (!branchId || !empId) return
    const load = async () => {
      try {
        setLoading(true)
        const res = await empAddressService.getAll(branchId, empId, pageNumber, pageSize)
        setAddresses(res.data ?? [])
        setTotalRecords(res.totalRecords ?? 0)
      } catch (e: any) {
        const status = e?.response?.status
        const msg = (e?.response?.data?.message ?? e?.message ?? '').toLowerCase()
        const isNoData = status === 404 || /no data|not found|no records/.test(msg)
        setAddresses([])
        setTotalRecords(0)
        if (!isNoData) {
          toast.error(e?.response?.data?.message ?? ta('fetchFailed'))
        }
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [branchId, empId, pageNumber, pageSize])

  useEffect(() => {
    if (empId) return
    setAddresses([])
    setTotalRecords(0)
    setSearchQuery('')
    setPageNumber(1)
    setPendingAdd(false)
  }, [empId])

  useEffect(() => {
    if (!pendingAdd || !empId) return
    setPendingAdd(false)
    navigate(`/emp-addresses/new?empId=${empId}`)
  }, [pendingAdd, empId, navigate])

  const filteredAddresses = useMemo(() => {
    if (!searchQuery.trim()) return addresses
    const q = searchQuery.toLowerCase()
    return addresses.filter(
      (a) =>
        String(a.homePhoneNumber ?? '').toLowerCase().includes(q) ||
        (a.addressDetail ?? '').toLowerCase().includes(q)
    )
  }, [addresses, searchQuery])

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize))

  const visibleColumnCount =
    [
      columnVisibility.homePhone,
      columnVisibility.addressDetail,
      columnVisibility.createdDate,
      columnVisibility.action,
    ].filter(Boolean).length || 1

  const formatDate = (d?: string) => {
    if (!d) return '—'
    try {
      return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' })
    } catch {
      return String(d).slice(0, 10)
    }
  }

  const handleAdd = () => navigate(`/emp-addresses/new?empId=${empId}`)
  const handleAddClick = () => {
    if (empId) return handleAdd()
    setPendingAdd(true)
    openEmployeePicker()
  }

  const handleEdit = (id: number) => navigate(`/emp-addresses/${id}/edit?empId=${empId}`)

  const handleDelete = async (id: number) => {
    const confirmed = await showDeleteConfirm(ta('deleteTitle'), ta('deleteConfirm'))
    if (!confirmed) return
    setDeletingId(id)
    try {
      await empAddressService.delete(id)
      await showSuccess(ta('deletedTitle'), ta('deletedMessage'))
      if (branchId && empId) {
        const res = await empAddressService.getAll(branchId, empId, pageNumber, pageSize)
        setAddresses(res.data ?? [])
        setTotalRecords(res.totalRecords ?? 0)
      }
    } catch (e: any) {
      await showError(ta('deleteFailedTitle'), e?.response?.data?.message ?? e?.message ?? ta('deleteFailedMessage'))
    } finally {
      setDeletingId(null)
    }
  }

  const handleCsv = () => {
    const headers = [ta('columnHomePhone'), ta('columnAddressDetail'), ta('columnCreatedDate')]
    const rows = filteredAddresses.map((a) =>
      [
        String(a.homePhoneNumber ?? ''),
        (a.addressDetail ?? '').replace(/\r?\n/g, ' '),
        a.createdDate ? formatDate(a.createdDate) : '',
      ]
        .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
        .join(',')
    )
    const csv = [headers.map((h) => `"${h.replace(/"/g, '""')}"`).join(','), ...rows].join('\r\n')
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = ta('csvFileName')
    a.click()
    URL.revokeObjectURL(url)
    toast.success(ta('csvDownloaded'))
  }

  const handleCopy = () => {
    const text = filteredAddresses
      .map((a) =>
        [
          a.homePhoneNumber ?? '',
          a.addressDetail ?? '',
          a.createdDate ? formatDate(a.createdDate) : '',
        ].join('\t')
      )
      .join('\n')
    void navigator.clipboard.writeText(text).then(() => toast.success(ta('copiedToClipboard')))
  }

  const handlePrint = () => window.print()

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title={ta('listTitle')} description={ta('listDescriptionShort')} />
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground">
            {ta('selectBranchFirst')}
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <EmployeeListScopeDialog
        open={pickerOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setPendingAdd(false)
          handlePickerOpenChange(nextOpen)
        }}
        branchId={branchId}
        onConfirm={confirmEmployee}
        currentEmpId={empId}
      />
      <PageHeader
        title={ta('listTitle')}
        description={
          empId
            ? `${ta('listDescription')} · ${t('employeeListScope.selectedEmployee', { id: empId })}`
            : ta('listDescription')
        }
        action={{ label: t('common.add'), onClick: handleAddClick, icon: Plus, disabled: !empId }}
      >
        <Button type="button" variant="outline" size="sm" className="gap-2" onClick={openEmployeePicker}>
          <Users className="h-4 w-4" />
          {t('employeeListScope.changeEmployee')}
        </Button>
      </PageHeader>

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0 pb-2">
          <div className="relative w-64 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('common.search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              disabled={!empId}
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={!empId}>
                  <Columns className="w-4 h-4 mr-2" />
                  {ta('columnVisibility')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[220px]">
                <DropdownMenuCheckboxItem
                  checked={columnVisibility.homePhone}
                  onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, homePhone: !!v }))}
                >
                  {ta('columnHomePhone')}
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={columnVisibility.addressDetail}
                  onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, addressDetail: !!v }))}
                >
                  {ta('columnAddressDetail')}
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={columnVisibility.createdDate}
                  onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, createdDate: !!v }))}
                >
                  {ta('columnCreatedDate')}
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={columnVisibility.action}
                  onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, action: !!v }))}
                >
                  {ta('columnAction')}
                </DropdownMenuCheckboxItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" size="sm" onClick={handleCsv} disabled={!empId}>
              <Download className="w-4 h-4 mr-2" />
              {ta('csv')}
            </Button>
            <Button variant="outline" size="sm" onClick={handleCopy} disabled={!empId}>
              <Copy className="w-4 h-4 mr-2" />
              {ta('copy')}
            </Button>
            <Button variant="outline" size="sm" onClick={handlePrint} disabled={!empId}>
              <Printer className="w-4 h-4 mr-2" />
              {ta('print')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                  {columnVisibility.homePhone ? (
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {ta('columnHomePhone')}
                    </TableHead>
                  ) : null}
                  {columnVisibility.addressDetail ? (
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {ta('columnAddressDetail')}
                    </TableHead>
                  ) : null}
                  {columnVisibility.createdDate ? (
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {ta('columnCreatedDate')}
                    </TableHead>
                  ) : null}
                  {columnVisibility.action ? (
                    <TableHead className="h-12 px-4 w-[100px] text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {ta('columnAction')}
                    </TableHead>
                  ) : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {!empId ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumnCount} className="text-center py-10 text-muted-foreground">
                      {t('employeeListScope.dialogDescription', {
                        defaultValue:
                          'Choose an employee to load their records. You can change this later from the list page.',
                      })}
                    </TableCell>
                  </TableRow>
                ) : loading ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumnCount} className="p-0">
                      <ListLoader message={ta('loading')} />
                    </TableCell>
                  </TableRow>
                ) : filteredAddresses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumnCount} className="text-center py-8 text-muted-foreground">
                      {ta('noData')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAddresses.map((addr) => (
                    <TableRow
                      key={addr.id}
                      className="border-b hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors"
                    >
                      {columnVisibility.homePhone ? (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle font-medium">
                          {addr.homePhoneNumber ?? '—'}
                        </TableCell>
                      ) : null}
                      {columnVisibility.addressDetail ? (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle max-w-[240px]">
                          <span className="line-clamp-2 break-words">{addr.addressDetail ?? '—'}</span>
                        </TableCell>
                      ) : null}
                      {columnVisibility.createdDate ? (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">
                          {formatDate(addr.createdDate)}
                        </TableCell>
                      ) : null}
                      {columnVisibility.action ? (
                        <TableCell className="px-4 py-2 text-center align-middle">
                          <div className="flex justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(addr.id!)}
                              className="h-8 w-8 p-0 hover:bg-primary/10 dark:hover:bg-primary/20"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(addr.id!)}
                              disabled={deletingId === addr.id}
                              className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-4 mt-4 text-sm text-muted-foreground">
            <div>
              {t('empAddress.paginationShowing', {
                from: totalRecords === 0 ? 0 : (pageNumber - 1) * pageSize + 1,
                to: Math.min(pageNumber * pageSize, totalRecords),
                total: totalRecords,
              })}
            </div>
            <div className="flex items-center gap-2">
              <Select
                value={String(pageSize)}
                onValueChange={(v) => {
                  setPageSize(Number(v))
                  setPageNumber(1)
                }}
                disabled={!empId}
              >
                <SelectTrigger className="h-8 w-[80px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[10, 25, 50].map((size) => (
                    <SelectItem key={size} value={String(size)}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  disabled={!empId || pageNumber <= 1}
                  onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span>
                  {t('empAddress.pageSlash', { page: pageNumber, totalPages })}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  disabled={!empId || pageNumber >= totalPages}
                  onClick={() => setPageNumber((p) => p + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

